import {
  API,
  Card,
  CheckBoxGroup,
  Help,
  ViewHeader,
  useGet
} from '@enkla-gui'
import React, { useEffect, useState } from 'react'

import ReportFilters from '../../components/report/ReportFilters'
import ReportPreview from '../../components/report/ReportPreview'
import { icons } from '@config'
import { useHistory } from 'react-router-dom'

export default function ReportCreate() {
  const [selectedModel, setSelectedModel] = useState('')
  const [selectedFields, setSelectedFields] = useState([])
  const [selectedRelationships, setSelectedRelationships] = useState([])
  const [filters, setFilters] = useState([])
  const [showPreview, setShowPreview] = useState(false)
  const [fields, setFields] = useState([])
  const [relationships, setRelationships] = useState([])
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [relationshipsLoading, setRelationshipsLoading] = useState(false)
  const [loadedReport, setLoadedReport] = useState(null)

  // Get available models
  const { data: models, loading: modelsLoading } = useGet({
    endpoint: 'api/report/models'
  })

  // Check if we have a premade config from sessionStorage
  useEffect(() => {
    const storedConfig = sessionStorage.getItem('premadeReportConfig')
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig)
        setLoadedReport(config)
        loadPremadeReport(config)

        // Clear the stored config to prevent reloading on refresh
        sessionStorage.removeItem('premadeReportConfig')
      } catch (error) {
        console.error('Error parsing premade config from sessionStorage:', error)
        sessionStorage.removeItem('premadeReportConfig')
      }
    }
  }, []) // Remove location.state dependency since we're not using it anymore

  // Reset selections when model changes
  useEffect(() => {
    if (selectedModel) {
      setSelectedFields([])
      setSelectedRelationships([])
      setFilters([])
      setShowPreview(false)

      // Fetch relationships for selected model
      setRelationshipsLoading(true)
      API.get(`api/report/models/${selectedModel}/relationships`)
        .then(data => {
          setRelationships(data || [])
          setRelationshipsLoading(false)
        })
        .catch(error => {
          console.error('Error fetching relationships:', error)
          setRelationships([])
          setRelationshipsLoading(false)
        })

      // Initial fetch of fields (without relationships)
      fetchFields([])
    } else {
      setFields([])
      setRelationships([])
      setFieldsLoading(false)
      setRelationshipsLoading(false)
    }
  }, [selectedModel])

  // Fetch fields when relationships change
  useEffect(() => {
    if (selectedModel) {
      fetchFields(selectedRelationships)
    }
  }, [selectedRelationships])

  const fetchFields = (relationships) => {
    setFieldsLoading(true)
    const params = relationships.length > 0 ? { relationships } : {}

    API.post(`api/report/models/${selectedModel}/fields`, params)
      .then(data => {
        const newFields = data || []
        setFields(newFields)

        // Filter out selected fields that are no longer available
        const availableFieldKeys = newFields.map(f => f.key)
        const validSelectedFields = selectedFields.filter(fieldKey =>
          availableFieldKeys.includes(fieldKey)
        )

        // Only update selected fields if some were removed
        if (validSelectedFields.length !== selectedFields.length) {
          setSelectedFields(validSelectedFields)
        }

        setFieldsLoading(false)
      })
      .catch(error => {
        console.error('Error fetching fields:', error)
        setFields([])
        setFieldsLoading(false)
      })
  }

  const loadPremadeReport = (reportConfig) => {
    // Load the premade report configuration
    setSelectedModel(reportConfig.model)
    setShowPreview(false)

    // Clear existing state
    setSelectedFields([])
    setSelectedRelationships([])
    setFilters([])

    // Fetch relationships first, then fields, then apply selections
    setRelationshipsLoading(true)
    API.get(`api/report/models/${reportConfig.model}/relationships`)
      .then(relationshipsData => {
        setRelationships(relationshipsData || [])
        setRelationshipsLoading(false)

        // Now fetch fields with the relationships from the config
        setFieldsLoading(true)
        const params = reportConfig.relationships?.length > 0 ? { relationships: reportConfig.relationships } : {}

        return API.post(`api/report/models/${reportConfig.model}/fields`, params)
      })
      .then(fieldsData => {
        setFields(fieldsData || [])
        setFieldsLoading(false)

        // Now apply the premade report selections
        setSelectedFields(reportConfig.fields || [])
        setSelectedRelationships(reportConfig.relationships || [])
        setFilters(reportConfig.filters || [])

        // Auto-show preview after everything is loaded
        setTimeout(() => {
          setShowPreview(true)
        }, 100)
      })
      .catch(error => {
        console.error('Error loading premade report:', error)
        setRelationships([])
        setFields([])
        setRelationshipsLoading(false)
        setFieldsLoading(false)
      })
  }

  const handleModelChange = (value) => {
    setSelectedModel(value)
    setLoadedReport(null) // Clear loaded report when manually changing model
  }

  const handleFieldChange = (fieldKeys) => {
    setSelectedFields(fieldKeys)
  }

  const handleRelationshipChange = (relationshipKeys) => {
    setSelectedRelationships(relationshipKeys)
    setShowPreview(false)
    // Don't reset field selections - let the useEffect handle field updates
  }

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters)
    // Reset preview when filters change
    setShowPreview(false)
  }

  const handlePreview = () => {
    if (selectedFields.length > 0) {
      setShowPreview(true)
    }
  }

  const canPreview = selectedModel && selectedFields.length > 0

  return (
    <>
      <ViewHeader
        title={loadedReport ? `${loadedReport.name} (Anpassad)` : 'Anpassad Rapport'}
        icon={loadedReport ? icons.star : icons.plus}
        subtitle={loadedReport ? 'Fördefinierad rapport laddad för anpassning' : 'Skapa rapport efter dina behov'}
      />

      {/* Custom Report Builder */}
      <div className='row'>
        <div className='col-12 col-xl-4 col-lg-5'>
          <Card title='1. Välj datatyp' icon={icons.database}>
            <div className='form-group'>
              <label>Välj datatyp</label>
              <select
                className='form-select'
                value={selectedModel}
                onChange={(e) => handleModelChange(e.target.value)}
              >
                <option value=''>Välj datatyp...</option>
                {models?.map(model => (
                  <option key={model.key} value={model.key}>
                    {model.label}
                  </option>
                )) || []}
              </select>
            </div>

            {selectedModel && (
              <>
                <hr />
                <Help type='info'>
                  <p>Du har valt <strong>{models?.find(m => m.key === selectedModel)?.label}</strong>.</p>
                  <p>Välj nu vilka fält och relationer du vill inkludera i rapporten.</p>
                </Help>
              </>
            )}
          </Card>

          {selectedModel && relationships && relationships.length > 0 && (
            <Card title='2. Välj relationer (valfritt)' icon={icons.link} loading={relationshipsLoading}>
              <CheckBoxGroup
                values={relationships.map(rel => ({
                  key: rel.label,
                  value: rel.key
                }))}
                selected={selectedRelationships}
                onChange={handleRelationshipChange}
                groupId='relationships'
              />
              <hr />
              <Help type='info'>
                <p>Relationer låter dig inkludera data från relaterade objekt.</p>
                <p>Till exempel: från uthyrningar kan du inkludera hyresgästdata.</p>
                <p><strong>Obs:</strong> När du väljer relationer uppdateras fältlistan automatiskt.</p>
              </Help>
            </Card>
          )}

          {selectedModel && (
            <Card title='3. Välj fält' icon={icons.columns} loading={fieldsLoading}>
              {fields && fields.length > 0 ? (
                <CheckBoxGroup
                  values={fields.map(field => ({
                    key: field.label,
                    value: field.key
                  }))}
                  selected={selectedFields}
                  onChange={handleFieldChange}
                  groupId='fields'
                />
              ) : (
                <p className='text-muted'>Inga fält tillgängliga</p>
              )}

              {selectedRelationships.length > 0 && (
                <Help type='success'>
                  <p>Fält från både huvudobjektet och valda relationer visas ovan.</p>
                </Help>
              )}
            </Card>
          )}

          {selectedModel && (
            <Card title='4. Filter (valfritt)' icon={icons.filter}>
              <ReportFilters
                model={selectedModel}
                fields={fields || []}
                filters={filters}
                onChange={handleFiltersChange}
              />
            </Card>
          )}

          <Card>
            <div className='d-flex gap-2'>
              <button
                className={`btn btn-primary ${!canPreview ? 'disabled' : ''}`}
                onClick={handlePreview}
                disabled={!canPreview}
              >
                <i className={icons.eye}></i> Förhandsgranska
              </button>
            </div>

            {!canPreview && (
              <>
                <hr />
                <Help type='warning'>
                  <p>Välj minst en datatyp och ett fält för att kunna förhandsgranska rapporten.</p>
                  <p>Exportfunktionen finns tillgänglig i förhandsgranskningsrutan.</p>
                </Help>
              </>
            )}
          </Card>
        </div>

        <div className='col-12 col-xl-8 col-lg-7'>
          {showPreview && canPreview ? (
            <ReportPreview
              model={selectedModel}
              fields={selectedFields}
              relationships={selectedRelationships}
              filters={filters}
            />
          ) : (
            <Card>
              <div className='text-center text-muted py-5'>
                <i className={`${icons.chart} fa-3x mb-3`}></i>
                <h5>Rapportförhandsgranskning</h5>
                <p>Välj fält och klicka på "Förhandsgranska" för att se rapporten.</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
